/**
 * useSubjectExtraction — single source of truth for polling the
 * background OCR extraction status, used by BOTH the PF flow
 * (PhaseDocuments) and the PM phases (CompanyData CSF dropzone,
 * Representante, Apoderado, Beneficiarios).
 *
 * Why a shared hook (and not duplicated logic per phase):
 *  - One place to fix bugs in polling (404 retries, in-flight
 *    re-entry guard, hard timeout).
 *  - Subject scoping is plumbed through to the endpoint via the
 *    optional query params introduced in 2026-06-18 — the hook just
 *    forwards them. The endpoint's default behaviour (no params)
 *    stays unchanged so PF doesn't regress.
 *  - Plans without OCR (free / pro-lite) skip polling entirely;
 *    the hook short-circuits to {ocrSkipped: true} so the UI can
 *    show "captura manual" directly without making the user wait
 *    45s for a doom timeout.
 *
 * Params (object):
 *   draftId             — required
 *   subjectType         — optional. "company"|"representative"|"apoderado"|"beneficial_owner"
 *   subjectDraftId      — optional. "rep-main" | "apo-main" | "bo-1" …
 *   enabled             — required. True only when something has
 *                         been uploaded for this subject; false
 *                         disables the polling loop.
 *   ocrEnabled          — required. From /vigencia-rules. If false
 *                         the hook short-circuits to ocrSkipped=true.
 *   refreshKey          — bump to force a fresh poll cycle after a
 *                         new upload completes.
 *   maxPollSeconds      — default 45. Hard ceiling.
 *
 * Returns:
 *   {
 *     documents,         // { docType → { status, data, error } }
 *     mergedData,        // merged fields ready to pre-fill the form
 *     allDone,
 *     anyFailed,
 *     elapsedSec,
 *     isExtracting,
 *     ocrSkipped,        // true on plans without OCR — UI shows manual capture
 *   }
 */
import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;
const DEFAULT_MAX_POLL_SECONDS = 45;

export default function useSubjectExtraction({
  draftId,
  subjectType = null,
  subjectDraftId = null,
  enabled,
  ocrEnabled,
  refreshKey = 0,
  maxPollSeconds = DEFAULT_MAX_POLL_SECONDS,
}) {
  const [state, setState] = useState({
    documents: {},
    mergedData: {},
    allDone: false,
    anyFailed: false,
    reviewFields: [],
  });
  const [startedAt, setStartedAt] = useState(null);
  const [elapsedSec, setElapsedSec] = useState(0);

  const timerRef = useRef(null);
  const inFlightRef = useRef(false);
  const consecutive404sRef = useRef(0);
  const seqRef = useRef(0);

  const url = (() => {
    if (!draftId) return null;
    const qs = new URLSearchParams();
    if (subjectType) qs.set("subject_type", subjectType);
    if (subjectDraftId) qs.set("subject_draft_id", subjectDraftId);
    const q = qs.toString();
    return `${API}/api/customer-drafts/${draftId}/extraction-status${q ? `?${q}` : ""}`;
  })();

  const pollOnce = useCallback(async () => {
    if (!url) return;
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    const seq = ++seqRef.current;
    try {
      const res = await axios.get(url, { withCredentials: true });
      consecutive404sRef.current = 0;
      const data = res.data || {};
      // PR1.5 safeguard: aggregate ``needs_review_fields`` across every
      // document of this draft/subject. ``pipeline_info`` may be partial
      // or absent (failure paths skip ``model_used`` /
      // ``needs_review_fields``) — use ``.get()``-equivalent guards on
      // every hop. Empty / missing → no fields to flag, behaviour
      // identical to pre-1.5.
      const reviewSet = new Set();
      const docs = data.documents || {};
      Object.values(docs).forEach((doc) => {
        const info = (doc && doc.pipeline_info) || {};
        const fields = Array.isArray(info.needs_review_fields)
          ? info.needs_review_fields
          : [];
        fields.forEach((f) => {
          if (typeof f === "string" && f) reviewSet.add(f);
        });
      });
      setState({
        documents: docs,
        mergedData: data.merged_data || {},
        allDone: !!data.all_done,
        anyFailed: !!data.any_failed,
        reviewFields: Array.from(reviewSet),
      });
      if (data.all_done) return;
      timerRef.current = setTimeout(pollOnce, 1500);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 404 || status === 405) {
        consecutive404sRef.current += 1;
        if (consecutive404sRef.current >= 2) {
          setState((p) => ({ ...p, allDone: true, anyFailed: true }));
          console.log(`[useSubjectExtraction#${seq}] STOP — 2x 404`);
          return;
        }
      }
      // Network / 5xx — retry with longer delay.
      timerRef.current = setTimeout(pollOnce, 3000);
    } finally {
      inFlightRef.current = false;
    }
  }, [url]);

  // Start polling whenever something becomes available to poll for.
  useEffect(() => {
    // Plans without OCR: don't poll, don't make the user wait.
    if (!ocrEnabled) return;
    if (!enabled || state.allDone || !url) return;
    if (!startedAt) setStartedAt(Date.now());
    pollOnce();
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [enabled, ocrEnabled, state.allDone, url, refreshKey, pollOnce, startedAt]);

  // Reset polling on a refreshKey bump so a NEW upload gets a fresh
  // 45s window (otherwise the timer keeps counting from the very
  // first upload and could cut off a fresh extraction in <1s).
  useEffect(() => {
    if (refreshKey === 0) return;
    if (!ocrEnabled) return;
    setStartedAt(Date.now());
    setElapsedSec(0);
    setState((p) => ({ ...p, allDone: false, anyFailed: false }));
  }, [refreshKey, ocrEnabled]);

  // Tick elapsed-seconds while extracting; bail at maxPollSeconds.
  useEffect(() => {
    if (!ocrEnabled) return;
    if (!startedAt || state.allDone) return;
    const id = setInterval(() => {
      const e = Math.floor((Date.now() - startedAt) / 1000);
      setElapsedSec(e);
      if (e >= maxPollSeconds) {
        if (timerRef.current) {
          clearTimeout(timerRef.current);
          timerRef.current = null;
        }
        setState((prev) => {
          const newDocs = {};
          let anyTimedOut = false;
          for (const [k, v] of Object.entries(prev.documents || {})) {
            if (v.status === "processing") {
              newDocs[k] = {
                status: "failed",
                data: null,
                error: "Tiempo de extracción agotado. Podrás completar manualmente.",
              };
              anyTimedOut = true;
            } else {
              newDocs[k] = v;
            }
          }
          return {
            documents: newDocs,
            mergedData: prev.mergedData,
            allDone: true,
            anyFailed: prev.anyFailed || anyTimedOut,
            reviewFields: prev.reviewFields || [],
          };
        });
      }
    }, 1000);
    return () => clearInterval(id);
  }, [startedAt, state.allDone, ocrEnabled, maxPollSeconds]);

  const isExtracting = ocrEnabled && enabled && !state.allDone &&
                       Object.keys(state.documents || {}).length > 0;

  return {
    documents: state.documents,
    mergedData: state.mergedData,
    allDone: state.allDone,
    anyFailed: state.anyFailed,
    // PR1.5 safeguard: callers (PhaseDocuments, PhaseRepresentative,
    // etc.) read this set to render an "verifica" indicator next to
    // pre-filled CURP / RFC / voter_key etc. when the extractor that
    // produced them was a weak-precision fallback (gpt-4o-mini today).
    // Always returns an Array — never undefined — so consumers can do
    // ``reviewFields.includes("curp")`` without a null-guard.
    reviewFields: state.reviewFields || [],
    elapsedSec,
    isExtracting,
    ocrSkipped: !ocrEnabled,
  };
}
